import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Receipt, Plus, Trash, CheckCircle, XCircle, HourglassSimple, X, Eye, FilePdf, Paperclip } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useSearch } from '../components/VerticalTabs';
import ReceiptOcrButton from '../components/expenses/ReceiptOcrButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['carburant', 'outillage', 'fourniture', 'repas', 'transport', 'hebergement', 'telephone', 'vetement', 'autre'];

const EmployeeExpensesPage = ({ embedded, paidBy = 'personal' }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isCompanyCard = paidBy === 'company_card';
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [previewImg, setPreviewImg] = useState(null);
  const [form, setForm] = useState({
    description: '', amount_ht: '', tva_rate: 20,
    amount_ht_2: '', tva_rate_2: 0,
    invoice_number: '',
    date: '', category: 'autre', supplier: '', receipt: null,
    project_id: '',
  });
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    // Load active projects for the optional "Affecter au chantier" select
    axios.get(`${API_URL}/api/projects`, { withCredentials: true })
      .then((r) => setProjects((r.data || []).filter((p) => !p.archived)))
      .catch(() => setProjects([]));
  }, []);

  const computedTVA = (parseFloat(form.amount_ht) || 0) * ((form.tva_rate || 0) / 100);
  const computedTVA2 = (parseFloat(form.amount_ht_2) || 0) * ((form.tva_rate_2 || 0) / 100);
  const computedTTC = (parseFloat(form.amount_ht) || 0) + computedTVA + (parseFloat(form.amount_ht_2) || 0) + computedTVA2;

  // Map Gemini OCR result -> form fields
  const applyOcrResult = (d) => {
    if (!d) return;
    const updates = {};
    if (d.supplier) updates.supplier = d.supplier;
    if (d.invoice_number) updates.invoice_number = d.invoice_number;
    if (d.date) updates.date = d.date;
    if (Array.isArray(d.tva_lines) && d.tva_lines.length > 0) {
      const l1 = d.tva_lines[0];
      if (l1?.amount_ht != null) updates.amount_ht = String(Number(l1.amount_ht) || '');
      if (l1?.rate != null) updates.tva_rate = Number(l1.rate) || 20;
      if (d.tva_lines.length > 1) {
        const l2 = d.tva_lines[1];
        if (l2?.amount_ht != null) updates.amount_ht_2 = String(Number(l2.amount_ht) || '');
        if (l2?.rate != null) updates.tva_rate_2 = Number(l2.rate) || 0;
      }
    } else if (d.amount_ht_total != null) {
      updates.amount_ht = String(Number(d.amount_ht_total) || '');
    }
    if (d.categorie && CATEGORIES.includes(d.categorie)) updates.category = d.categorie;
    if (!form.description) {
      const head = d.articles?.[0]?.label || d.supplier || '';
      if (head) updates.description = head;
    }
    setForm(prev => ({ ...prev, ...updates }));
  };

  const fetchExpenses = useCallback(async () => {
    try {
      let url = `${API_URL}/api/employee-expenses?month=${selectedMonth}&paid_by=${paidBy}`;
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      const res = await axios.get(url, { withCredentials: true });
      setExpenses(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, filterStatus, paidBy, API_URL]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append('description', form.description);
      fd.append('amount_ht', form.amount_ht);
      fd.append('tva_rate', form.tva_rate);
      fd.append('amount_ht_2', form.amount_ht_2 || 0);
      fd.append('tva_rate_2', form.tva_rate_2 || 0);
      fd.append('invoice_number', form.invoice_number || '');
      fd.append('date', form.date);
      fd.append('category', form.category);
      fd.append('supplier', form.supplier);
      fd.append('paid_by', paidBy);
      if (form.project_id) fd.append('project_id', form.project_id);
      if (form.receipt) fd.append('receipt', form.receipt);
      await axios.post(`${API_URL}/api/employee-expenses`, fd, { withCredentials: true });
      toast.success(isCompanyCard ? 'Achat société enregistré' : 'Dépense soumise pour validation');
      setShowModal(false);
      setForm({ description: '', amount_ht: '', tva_rate: 20, amount_ht_2: '', tva_rate_2: 0, invoice_number: '', date: '', category: 'autre', supplier: '', receipt: null, project_id: '' });
      fetchExpenses();
    } catch (error) {
      toast.error('Erreur lors de la soumission');
    }
  };

  const handleDownloadAttachmentsPdf = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employee-expenses/attachments-pdf?month=${selectedMonth}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `notes_frais_pieces_jointes_${selectedMonth}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aucune piece jointe pour ce mois');
    }
  };

  const handleValidate = async (expenseId, action) => {
    try {
      await axios.put(`${API_URL}/api/employee-expenses/${expenseId}/validate`, { action }, { withCredentials: true });
      toast.success(action === 'approve' ? 'Dépense validée et intégrée aux notes de frais' : 'Dépense rejetée');
      fetchExpenses();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await axios.delete(`${API_URL}/api/employee-expenses/${id}`, { withCredentials: true });
      toast.success('Dépense supprimée');
      fetchExpenses();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const statusBadge = (status) => {
    switch (status) {
      case 'approved': return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: '#E8F5E9', color: '#2E7D32' }}><CheckCircle size={14} /> Validée</span>;
      case 'rejected': return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: '#FFEBEE', color: '#C62828' }}><XCircle size={14} /> Rejetée</span>;
      default: return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: '#FFF8E1', color: '#F57F17' }}><HourglassSimple size={14} /> En attente</span>;
    }
  };

  const { searchTerm } = useSearch();
  const filteredExpenses = expenses.filter(e => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (e.description || '').toLowerCase().includes(s) || (e.user_name || '').toLowerCase().includes(s) || (e.category || '').toLowerCase().includes(s);
  });
  const totalHT = filteredExpenses.reduce((s, e) => s + e.amount_ht, 0);
  const totalTTC = filteredExpenses.reduce((s, e) => s + e.amount_ttc, 0);

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="employee-expenses-page">
      {!embedded && (
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">{isAdmin ? 'Notes de frais (Validation)' : 'Mes notes de frais'}</h1>
          <p className="page-subtitle">
            {expenses.length} dépense(s) - Total HT: {totalHT.toLocaleString('fr-FR')}€ - Total TTC: {totalTTC.toLocaleString('fr-FR')}€
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={() => window.open(`${API_URL}/api/employee-expenses/export/pdf`, '_blank')} data-testid="export-emp-expenses-pdf-btn">
            <FilePdf size={18} /> Export PDF
          </button>
          <button className="btn btn-secondary" onClick={handleDownloadAttachmentsPdf} data-testid="emp-download-attachments-btn" title="Telecharger toutes les pieces jointes du mois en 1 PDF">
            <Paperclip size={18} /> Pieces jointes
          </button>
          <button data-testid="add-employee-expense-btn" className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={18} weight="bold" /> Ajouter une dépense
          </button>
        </div>
      </div>
      )}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <p style={{ fontSize: '0.85rem', color: 'var(--muted-foreground, #5E5F66)' }}>
            {expenses.length} dépense(s) - Total HT: {totalHT.toLocaleString('fr-FR')}€ - Total TTC: {totalTTC.toLocaleString('fr-FR')}€
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-secondary" onClick={() => window.open(`${API_URL}/api/employee-expenses/export/pdf`, '_blank')} data-testid="export-emp-expenses-pdf-btn">
              <FilePdf size={18} /> Export PDF
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadAttachmentsPdf} data-testid="emp-download-attachments-emb-btn" title="Telecharger toutes les pieces jointes du mois en 1 PDF">
              <Paperclip size={18} /> Pieces jointes
            </button>
            <button data-testid="add-employee-expense-btn" className="btn btn-primary" onClick={() => setShowModal(true)}>
              <Plus size={18} weight="bold" /> Ajouter une dépense
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input type="month" className="form-input" style={{ width: '180px' }} value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger style={{ width: '180px' }}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="approved">Validées</SelectItem>
            <SelectItem value="rejected">Rejetées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="card">
        {filteredExpenses.length > 0 ? (
          <table className="data-table" data-testid="employee-expenses-table">
            <thead>
              <tr>
                {isAdmin && <th>Salarié</th>}
                <th>Date</th>
                <th>Catégorie</th>
                <th>Description</th>
                <th>HT</th>
                <th>TVA</th>
                <th>TTC</th>
                <th>Justificatif</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.map((exp) => (
                <tr key={exp.id}>
                  {isAdmin && <td style={{ fontWeight: '600' }}>{exp.user_name}</td>}
                  <td>{exp.date}</td>
                  <td>{exp.category}</td>
                  <td>{exp.description}</td>
                  <td>{exp.amount_ht.toLocaleString('fr-FR')}€</td>
                  <td>{exp.amount_tva.toLocaleString('fr-FR')}€</td>
                  <td style={{ fontWeight: '600' }}>{exp.amount_ttc.toLocaleString('fr-FR')}€</td>
                  <td>
                    {(exp.attachment?.url || exp.receipt_url) ? (
                      <div style={{ display: 'inline-flex', gap: 4 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => setPreviewImg(`${API_URL}${exp.attachment?.url || exp.receipt_url}`)} data-testid={`view-receipt-${exp.id}`}>
                          <Eye size={14} /> Voir
                        </button>
                        <a
                          href={`${API_URL}${exp.attachment?.url || exp.receipt_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary btn-sm"
                          title="Telecharger la piece jointe"
                          data-testid={`download-receipt-${exp.id}`}
                          style={{ display: 'inline-flex', alignItems: 'center' }}
                        >
                          <Paperclip size={14} />
                        </a>
                      </div>
                    ) : '-'}
                  </td>
                  <td>{statusBadge(exp.status)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {isAdmin && exp.status === 'pending' && (
                        <>
                          <button className="btn btn-primary btn-sm" onClick={() => handleValidate(exp.id, 'approve')} data-testid={`approve-expense-${exp.id}`}>
                            <CheckCircle size={14} />
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleValidate(exp.id, 'reject')} data-testid={`reject-expense-${exp.id}`}>
                            <XCircle size={14} />
                          </button>
                        </>
                      )}
                      {(exp.status === 'pending' || isAdmin) && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(exp.id)}>
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <Receipt size={48} className="empty-state-icon" />
            <p className="empty-state-text">Aucune note de frais pour cette période</p>
          </div>
        )}
      </div>

      {/* Add Expense Modal */}
      {showModal && (
        <div className="form-modal-overlay">
          <div className="form-modal-card" style={{ maxWidth: '520px' }}>
            <div className="form-modal-header">
              <h3 style={{ margin: 0, fontSize: '1.05rem' }}>
                {isCompanyCard ? 'Achat avec la carte société' : 'Ajouter une note de frais'}
              </h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
            </div>
            <form onSubmit={handleSubmit} className="form-modal-body">
              {/* OCR Photo with Gemini (multi-TVA) */}
              <div className="card" style={{ padding: '14px', marginBottom: '16px', backgroundColor: '#F4F8FF', border: '1px dashed #1D4ED8' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Receipt size={18} weight="duotone" style={{ color: '#1D4ED8' }} />
                  <span style={{ fontWeight: 700, fontSize: '0.86rem', color: '#1D4ED8' }}>Auto-remplir depuis une photo de ticket</span>
                </div>
                <ReceiptOcrButton
                  endpoint="/api/employee-expenses/ocr"
                  onResult={applyOcrResult}
                  onFilePicked={(file) => setForm(prev => ({ ...prev, receipt: file }))}
                />
                <div style={{ fontSize: '0.74rem', color: '#5E5F66', marginTop: 6 }}>
                  La photo sera enregistree comme piece jointe et restera telechargeable.
                </div>
                {form.receipt && <p style={{ fontSize: '0.75rem', color: '#2E7D32', marginTop: '6px', marginBottom: 0 }}>Fichier: {form.receipt.name}</p>}
              </div>

              <div className="form-group">
                <label className="form-label">Description *</label>
                <input type="text" className="form-input" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required placeholder="Ex: Plein de gasoil" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Catégorie</label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <label className="form-label">Fournisseur</label>
                  <input type="text" className="form-input" value={form.supplier} onChange={(e) => setForm({...form, supplier: e.target.value})} placeholder="Ex: Total Energies" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Affecter au chantier (optionnel)</label>
                <Select value={form.project_id || 'none'} onValueChange={(v) => setForm({ ...form, project_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="emp-expense-project-select"><SelectValue placeholder="Aucun chantier" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun chantier</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}{p.client_name ? ` — ${p.client_name}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">N° facture / ticket</label>
                  <input type="text" className="form-input" value={form.invoice_number} onChange={(e) => setForm({...form, invoice_number: e.target.value})} placeholder="Optionnel" data-testid="emp-expense-invoice-number" />
                </div>
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input type="date" className="form-input" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} required />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Base HT TVA 1 *</label>
                  <input type="number" step="0.01" className="form-input" value={form.amount_ht} onChange={(e) => setForm({...form, amount_ht: e.target.value})} required data-testid="expense-ht-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Taux TVA 1 %</label>
                  <input type="number" step="0.1" className="form-input" value={form.tva_rate} onChange={(e) => setForm({...form, tva_rate: parseFloat(e.target.value) || 20})} data-testid="expense-tva-input" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: '0.82rem', color: '#5E5F66' }}>Base HT TVA 2 (facultatif)</label>
                  <input type="number" step="0.01" className="form-input" value={form.amount_ht_2} onChange={(e) => setForm({...form, amount_ht_2: e.target.value})} data-testid="emp-expense-amount-ht-2" placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: '0.82rem', color: '#5E5F66' }}>Taux TVA 2 %</label>
                  <input type="number" step="0.1" className="form-input" value={form.tva_rate_2 || ''} onChange={(e) => setForm({...form, tva_rate_2: parseFloat(e.target.value) || 0})} data-testid="emp-expense-tva-rate-2" placeholder="0" />
                </div>
              </div>
              {(parseFloat(form.amount_ht) > 0 || parseFloat(form.amount_ht_2) > 0) && (
                <div data-testid="expense-ttc-auto" style={{ padding: '12px 16px', backgroundColor: '#E8F5E9', borderRadius: '6px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '0.8rem', color: '#2E7D32', display: 'flex', justifyContent: 'space-between' }}>
                    <span>TVA 1 ({form.tva_rate}%): <strong>{computedTVA.toFixed(2)} EUR</strong></span>
                    {parseFloat(form.amount_ht_2) > 0 && <span>TVA 2 ({form.tva_rate_2}%): <strong>{computedTVA2.toFixed(2)} EUR</strong></span>}
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1B5E20', marginTop: 4 }}>
                    Total TTC : {computedTTC.toFixed(2)} EUR
                  </div>
                </div>
              )}
            </form>
            <div className="form-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} style={{ flex: 1 }}>Annuler</button>
              <button type="button" className="btn btn-primary" onClick={handleSubmit} style={{ flex: 1 }} data-testid="submit-employee-expense">Soumettre</button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImg && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={() => setPreviewImg(null)}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }}>
            <button onClick={() => setPreviewImg(null)} style={{ position: 'absolute', top: '-40px', right: '0', color: 'white', background: 'none', border: 'none', cursor: 'pointer' }}><X size={32} /></button>
            <img src={previewImg} alt="Justificatif" style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '4px' }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeExpensesPage;
